/**
 * The running-timer chip — DESIGN.md §6.13.
 *
 * "a persistent header chip shows the running card + elapsed time everywhere
 * in the app; **Stop** creates an entry … and **Discard** doesn't."
 *
 * It lives in the shell rather than the card panel precisely because the point
 * is *everywhere*: a timer you can only see on the card you started it from is
 * a timer you forget you left running. Two corrections from the Long-Day
 * Review (SHELL-3, DAILY-7, MOB-9): it used to sit at the *bottom* of the nav,
 * which put it below the fold on desktop and off-screen entirely on a phone —
 * it now sits at the top of the sidebar and in the mobile header — and it
 * showed only `#12`, which is not enough to recognise what you left running,
 * so it shows the title too.
 *
 * Since #4 there may be several running at once, so this is a *stack* of
 * chips. Each one is independent — its own stop and its own discard — because
 * "stop the timer" is no longer an unambiguous instruction.
 */
import { Link } from '@tanstack/react-router';
import { Square, Timer, Trash2 } from 'lucide-react';
import { formatDurationCompact, type ActiveTimer } from '@slate/shared';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shell/tooltip';
import { useTimers, useTimerControls } from '@/features/time/api';
import { cn } from '@/lib/utils';

export function TimerChip({
  className,
  variant = 'full',
}: {
  className?: string;
  /** `rail` is the collapsed-sidebar form: icon and elapsed time only. */
  variant?: 'full' | 'rail';
}): JSX.Element | null {
  const { data: timers } = useTimers();

  if (!timers || timers.length === 0) return null;

  return (
    <div
      className={cn('flex min-w-0 flex-col gap-1', className)}
      // One group, so a screen reader announces "3 timers running" rather than
      // three unrelated status regions.
      role="group"
      aria-label={timers.length === 1 ? 'Running timer' : `${timers.length} running timers`}
    >
      {timers.map((timer) => (
        <OneTimer key={timer.taskId} timer={timer} variant={variant} />
      ))}
    </div>
  );
}

function OneTimer({
  timer,
  variant,
}: {
  timer: ActiveTimer;
  variant: 'full' | 'rail';
}): JSX.Element {
  const { stop, discard } = useTimerControls();

  const elapsed = formatDurationCompact(timer.elapsedMinutes);
  const label = `Timer running on card #${timer.taskNumber} ${timer.taskTitle} — ${elapsed}`;
  // §6.13 — a timer running past 12 h is more likely a forgotten night than a
  // long day, so it asks for attention rather than ticking on.
  const attention = timer.needsConfirmation
    ? 'border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400'
    : 'bg-card';

  if (variant === 'rail') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/card/$number"
            params={{ number: String(timer.taskNumber) }}
            aria-label={label}
            className={cn(
              'hover:bg-accent flex flex-col items-center gap-0.5 rounded-md border px-1 py-1.5 transition-colors',
              attention,
            )}
          >
            <Timer className="size-4" aria-hidden="true" />
            <span className="font-mono text-[0.7rem] tabular-nums leading-none">{elapsed}</span>
          </Link>
        </TooltipTrigger>
        <TooltipContent>
          <span className="truncate">
            #{timer.taskNumber} {timer.taskTitle}
          </span>
          <span className="font-mono tabular-nums">{elapsed}</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
        attention,
      )}
      role="status"
      aria-label={label}
    >
      <Timer className="size-3.5 shrink-0" aria-hidden="true" />
      <Link
        to="/card/$number"
        params={{ number: String(timer.taskNumber) }}
        className="min-w-0 flex-1 truncate hover:underline"
        title={`#${timer.taskNumber} ${timer.taskTitle}`}
      >
        <span className="text-muted-foreground">#{timer.taskNumber}</span> {timer.taskTitle}
      </Link>
      <span className="shrink-0 font-mono tabular-nums">{elapsed}</span>
      <Button
        variant="ghost"
        size="icon"
        className="size-6 shrink-0"
        aria-label="Stop the timer and log the time"
        title="Stop and log"
        disabled={stop.isPending}
        onClick={() => {
          if (
            timer.needsConfirmation &&
            !window.confirm(`This timer has been running for ${elapsed}. Log it?`)
          ) {
            return;
          }
          stop.mutate({ taskId: timer.taskId });
        }}
      >
        <Square className="size-3" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        // Phones have no room for both, and Discard is the rarer of the two —
        // it is still on the card itself.
        className="size-6 shrink-0 max-sm:hidden"
        aria-label="Discard the timer without logging"
        title="Discard"
        disabled={discard.isPending}
        onClick={() => {
          if (window.confirm('Discard this timer? Nothing will be logged.'))
            discard.mutate({ taskId: timer.taskId });
        }}
      >
        <Trash2 className="size-3" />
      </Button>
    </div>
  );
}
